/**
 * Tower system: a garrisoned defensive building looses ONE arrow at a time at the
 * nearest enemy in range, fired as a STREAM — one arrow every (reload ÷ garrison
 * count) — so a full tower keeps up a steady patter instead of one simultaneous
 * mega-volley that one-shots. More garrison ⇒ higher fire rate (same per-arrow
 * damage), so firepower still scales with how many units shelter inside. An empty
 * tower can't shoot.
 *
 * Towers have a Combat component but no Move, so the main combatSystem skips them
 * (it requires a transform+move). This runs right after combat, reusing its fresh
 * spatial index. Deterministic: nearestEnemyInRange uses a lower-eid tie-break.
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import { FIXED_DT } from "../types";
import { nearestEnemyInRange } from "./combat";

export function towerSystem(world: World, _reg: Registry): void {
  for (const [eid, gar] of world.garrison) {
    const b = world.building.get(eid);
    if (!b || b.isFoundation) continue;          // garrison carriers that aren't (finished) buildings → not towers
    const combat = world.combat.get(eid);
    if (!combat) continue;                        // a plain transport has a garrison but no attack
    const arrows = gar.occupants.length;          // each garrisoned unit contributes one arrow to the stream
    if (arrows === 0) { combat.cooldownLeft = 0; continue; } // empty: holds fire, ready to shoot when manned
    if (combat.cooldownLeft > 0) {
      combat.cooldownLeft = Math.max(0, combat.cooldownLeft - FIXED_DT);
      continue;
    }

    const target = nearestEnemyInRange(world, eid, combat.range);
    if (target === null) continue;
    const tr = world.transform.get(eid);
    const tTr = world.transform.get(target);
    const tHealth = world.health.get(target);
    if (!tr || !tTr || !tHealth) continue;

    // ONE arrow's worth of damage (class/building bonuses apply), then reload for the
    // staggered interval — N garrisoned units ⇒ an arrow every cooldown/N seconds.
    const tId = world.ident.get(target);
    let dmg = combat.damage;
    const bonus = tId?.cls ? combat.bonus?.[tId.cls] : undefined;
    if (bonus) dmg *= bonus;
    if (combat.bonusBuilding && world.building.has(target)) dmg *= combat.bonusBuilding;
    tHealth.hp -= Math.max(1, dmg - tHealth.armor);
    combat.cooldownLeft = combat.cooldown / arrows;

    const owner = world.owner.get(eid)?.player ?? 0;
    const color = world.players.get(owner)?.color ?? "#fff";
    world.emit({ type: "projectile", tick: 0, from: { ...tr.pos }, to: { ...tTr.pos }, color, kind: "arrow" });
    world.emit({ type: "hit", tick: 0, at: { ...tTr.pos } });
    if (tHealth.hp <= 0) {
      const meat = world.toCarcass(target);       // a slain animal → meat (parity with combat)
      if (!meat) world.markDead(target);
      world.emit({ type: "death", tick: 0, at: { ...tTr.pos } });
    }
  }
}
